import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import {
  calculateReferralStats,
  getReferralCharacterReward,
} from '@src/services/referral';
import { getReferral } from '@src/apis/referral';
import { formatNumber } from '@src/utils/number';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledReferralStats, StyledTooltip } from './index.style';
import StatCard from './StatCard';
import GiftButton from './GiftButton';

const ReferralStats = () => {
  const { getFeatureValue } = useFeatureFlags();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { user } = useSelector((state) => state.auth);
  const { referral } = user;
  const { earnedPoints = 0, spentPoints = 0 } = referral || {};
  const initPoints = earnedPoints - spentPoints;
  const [maxRewardCharacter, setMaxRewardCharacter] = useState(0);
  const [redeemedCharacters, setRedeemedCharacters] = useState(0);
  const stats = calculateReferralStats(user?.referral);

  const limitRedeemCharacterEnabled = getFeatureValue(
    FEATURE_KEYS.LIMIT_REDEEM_CHARACTER_PER_MONTH,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const renderButtonGift = () => (
    <StyledTooltip arrow title={t('claimRewards')} placement="top">
      <Box className="gift-wrapper">
        <GiftButton
          maxRewardCharacter={maxRewardCharacter}
          redeemedCharacters={redeemedCharacters}
        />
      </Box>
    </StyledTooltip>
  );

  useEffect(() => {
    const fetchReferral = async () => {
      const response = await getReferral();
      const {
        redeemedCharsThisMonth,
        earnedPoints: earnedPointsResponse = 0,
        spentPoints: spentPointsResponse = 0,
      } = response.result;
      const currentMaxRewardCharacter = getReferralCharacterReward({
        points: initPoints,
        redeemedCharsThisMonth,
        limitRedeemCharacterEnabled,
      });
      setRedeemedCharacters(redeemedCharsThisMonth);
      setMaxRewardCharacter(currentMaxRewardCharacter);
      dispatch(
        actions.auth.updateReferralStats({
          earnedPoints: earnedPointsResponse,
          spentPoints: spentPointsResponse,
        }),
      );
    };
    fetchReferral();
  }, [initPoints]);

  return (
    <StyledReferralStats>
      <div className="referral-stats-container">
        <div className="referral-stats-title">
          <Typography className="title-text">
            {t('referralStatsTitle')}
          </Typography>
        </div>
        <div className="referral-stats">
          <StatCard
            value={formatNumber(stats.totalUsers, language)}
            label={t('referralStatsTotalUsers')}
          />
          <StatCard
            value={formatNumber(stats.claimedChars, language)}
            label={t('referralStatsClaimedChars')}
          />
          <StatCard
            value={formatNumber(stats.unclaimedChars, language)}
            label={t('referralStatsUnclaimedChars')}
            isHighlight
            renderButtonGift={
              Boolean(stats?.unclaimedChars) && renderButtonGift
            }
          />
        </div>
      </div>
    </StyledReferralStats>
  );
};

export default ReferralStats;
